import { useState, useMemo, useEffect } from "react";
import {
  Box, Card, CardContent, Typography, Grid, Chip, Avatar,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Select, MenuItem, FormControl, InputLabel, Stack, Paper,
  CircularProgress, Alert,
} from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import MouseIcon from "@mui/icons-material/Mouse";
import StorefrontIcon from "@mui/icons-material/Storefront";
import PeopleIcon from "@mui/icons-material/People";
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer,
} from "recharts";
import { useAppSelector } from "../hooks";
import { getVendorClicks } from "../api";
import { Vendor, VendorClick } from "../types";

// ─── Types ────────────────────────────────────────────────────────────────────
type Platform = "iOS" | "Android" | "Windows" | "macOS" | "Web";

// ─── Theme ────────────────────────────────────────────────────────────────────
const dashTheme = createTheme({
  palette: {
    mode: "light",
    primary: { main: "#715939" },
    secondary: { main: "#8b6d46" },
    background: { default: "#faead7", paper: "#fff8f0" },
    text: { primary: "#2b2218", secondary: "#8b6d46" },
  },
  typography: {
    fontFamily: "'DM Mono', 'Courier New', monospace",
    h4: { fontFamily: "'Syne', sans-serif", fontWeight: 800, letterSpacing: "-0.03em" },
    h6: { fontFamily: "'Syne', sans-serif", fontWeight: 600 },
  },
  shape: { borderRadius: 12 },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundImage: "none",
          border: "1px solid rgba(113,89,57,0.12)",
          backgroundColor: "#fff8f0",
          boxShadow: "0 1px 3px rgba(113,89,57,0.08)",
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          color: "#715939",
          fontWeight: 600,
          fontSize: "0.7rem",
          letterSpacing: "0.1em",
          textTransform: "uppercase" as const,
          borderBottom: "1px solid rgba(113,89,57,0.15)",
          backgroundColor: "#fff8f0",
        },
        body: {
          borderBottom: "1px solid rgba(113,89,57,0.07)",
          fontSize: "0.8rem",
          color: "#604c35",
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        icon: { color: "#8b6d46" },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        notchedOutline: { borderColor: "rgba(113,89,57,0.25)" },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: { color: "#8b6d46" },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: { color: "#2b2218" },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: "none" },
      },
    },
  },
});

// ─── Constants ────────────────────────────────────────────────────────────────
const PLATFORM_COLORS: Record<Platform, string> = {
  iOS: "#c09c72",
  Android: "#8b6d46",
  Windows: "#715939",
  macOS: "#faead7",
  Web: "#604c35",
};

// ─── Custom Tooltip ───────────────────────────────────────────────────────────
interface TooltipEntry {
  name?: string;
  value?: number | string;
  color?: string;
}
interface CustomTooltipProps {
  active?: boolean;
  payload?: TooltipEntry[];
  label?: string;
}

interface VendorTooltipProps {
  active?: boolean;
  payload?: Array<{ payload?: { name: string; clicks: number; types?: Record<string, number> } }>;
  label?: string;
}

function VendorTooltip({ active, payload }: VendorTooltipProps) {
  if (!active || !payload?.length) return null;
  const data = payload[0]?.payload;
  if (!data) return null;
  return (
    <Paper sx={{ p: 1.5, border: "1px solid rgba(113,89,57,0.15)", bgcolor: "#fff8f0", minWidth: 160 }}>
      <Typography variant="caption" sx={{ color: "#715939", fontWeight: 700, display: "block", mb: 0.75 }}>
        {data.name}
      </Typography>
      <Stack direction="row" justifyContent="space-between" spacing={2} mb={0.5}>
        <Typography variant="caption" sx={{ color: "#8b6d46" }}>Unique users</Typography>
        <Typography variant="caption" sx={{ color: "#2b2218", fontWeight: 700 }}>{data.clicks}</Typography>
      </Stack>
      {data.types && Object.keys(data.types).length > 1 && (
        <>
          <Box sx={{ borderTop: "1px solid rgba(113,89,57,0.1)", my: 0.75 }} />
          {Object.entries(data.types)
            .sort((a, b) => b[1] - a[1])
            .map(([type, count]) => (
              <Stack key={type} direction="row" justifyContent="space-between" spacing={2}>
                <Typography variant="caption" sx={{ color: "#8b6d46" }}>{type}</Typography>
                <Typography variant="caption" sx={{ color: "#604c35" }}>{count}</Typography>
              </Stack>
            ))}
        </>
      )}
    </Paper>
  );
}

function CustomTooltip({ active, payload, label }: CustomTooltipProps) {
  if (!active || !payload?.length) return null;
  return (
    <Paper sx={{ p: 1.5, border: "1px solid rgba(113,89,57,0.15)", bgcolor: "#fff8f0", minWidth: 130 }}>
      <Typography variant="caption" sx={{ color: "#8b6d46", display: "block", mb: 0.5 }}>
        {label}
      </Typography>
      {payload.map((p) => (
        <Stack key={p.name} direction="row" justifyContent="space-between" spacing={2}>
          <Typography variant="caption" sx={{ color: p.color }}>{p.name}</Typography>
          <Typography variant="caption" sx={{ color: "#2b2218", fontWeight: 600 }}>{p.value}</Typography>
        </Stack>
      ))}
    </Paper>
  );
}

// ─── Stat Card ────────────────────────────────────────────────────────────────
interface StatCardProps {
  icon: React.ReactNode;
  label: string;
  value: string;
  sub?: string;
  accent?: string;
}

function StatCard({ icon, label, value, sub, accent = "#00E5A0" }: StatCardProps) {
  return (
    <Card sx={{ height: "100%", position: "relative", overflow: "hidden" }}>
      <Box sx={{
        position: "absolute", top: -20, right: -20,
        width: 100, height: 100, borderRadius: "50%",
        background: `radial-gradient(circle, ${accent}22 0%, transparent 70%)`,
      }} />
      <CardContent sx={{ p: 3 }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
          <Box>
            <Typography variant="caption" sx={{ color: "text.secondary", letterSpacing: "0.1em", textTransform: "uppercase", fontSize: "0.65rem" }}>
              {label}
            </Typography>
            <Typography variant="h4" sx={{ mt: 0.5, color: accent, fontSize: "2rem" }}>{value}</Typography>
            {sub && <Typography variant="caption" sx={{ color: "#8b6d46" }}>{sub}</Typography>}
          </Box>
          <Avatar sx={{ bgcolor: `${accent}18`, color: accent, width: 44, height: 44 }}>{icon}</Avatar>
        </Stack>
      </CardContent>
    </Card>
  );
}

// ─── Period Options ───────────────────────────────────────────────────────────
type PeriodKey = "7d" | "14d" | "30d" | "90d" | "180d" | "365d" | "all";

const PERIODS: { key: PeriodKey; label: string; days?: number }[] = [
  { key: "7d", label: "Last 7 days", days: 7 },
  { key: "14d", label: "Last 14 days", days: 14 },
  { key: "30d", label: "Last 30 days", days: 30 },
  { key: "90d", label: "Last 3 months", days: 90 },
  { key: "180d", label: "Last 6 months", days: 180 },
  { key: "365d", label: "Last year", days: 365 },
  { key: "all", label: "All time" },
];

const DATA_FLOOR = new Date("2024-10-01T00:00:00.000Z");

function getPeriodFrom(key: PeriodKey): string {
  const period = PERIODS.find((p) => p.key === key);
  if (!period || !period.days) return DATA_FLOOR.toISOString();
  const from = new Date();
  from.setDate(from.getDate() - period.days);
  // Never go earlier than Oct 1st
  return (from < DATA_FLOOR ? DATA_FLOOR : from).toISOString();
}

// ─── Dashboard ────────────────────────────────────────────────────────────────
export default function VendorClicksDashboard() {
  const [selectedVendor, setSelectedVendor] = useState<string>("all");
  const [period, setPeriod] = useState<PeriodKey>("30d");

  const [clicks, setClicks] = useState<VendorClick[]>([]);
  const vendors = useAppSelector(state => state.app.vendors);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch clicks whenever vendor or period changes
  useEffect(() => {
    setLoading(true);
    setError(null);

    getVendorClicks(
      selectedVendor === "all" ? undefined : selectedVendor,
      getPeriodFrom(period)
    )
      .then(setClicks)
      .catch((err: any) => setError(String(err)))
      .finally(() => setLoading(false));
  }, [selectedVendor, period]);

  // Enrich clicks with vendor name for display
  // If the clicked vendor is a subVendor, resolve up to the mainVendor for grouping
  const enriched = useMemo(() => {
    return clicks.map((c) => {
      const vendor = vendors.find((v) => v.id === c.vendorId);
      const resolvedVendor = vendor?.mainVendor ?? vendor;
      return {
        ...c,
        vendorName: resolvedVendor?.name ?? c.vendorId,
      };
    });
  }, [clicks, vendors]);

  // Daily unique users
  const dailyData = useMemo(() => {
    const map: Record<string, Set<string>> = {};
    enriched.forEach((c) => {
      const day = c.clickedAt.slice(5, 10);
      if (!map[day]) map[day] = new Set();
      map[day].add(c.clientId);
    });
    return Object.entries(map).sort().map(([date, clients]) => {
      const [month, day] = date.split('-');
      return { date: `${day}/${month}`, users: clients.size };
    });
  }, [enriched]);

  // Platform breakdown
  const platformData = useMemo(() => {
    const map: Record<string, number> = {};
    enriched.forEach((c) => { map[c.platform] = (map[c.platform] || 0) + 1; });
    return Object.entries(map)
      .sort((a, b) => b[1] - a[1])
      .map(([name, value]) => ({ name, value }));
  }, [enriched]);

  // Vendor bar — unique users per vendor, with type breakdown
  const vendorData = useMemo(() => {
    // map: vendorName -> { clients: Set<clientId>, types: { typeName -> Set<clientId> } }
    const map: Record<string, { clients: Set<string>; types: Record<string, Set<string>> }> = {};
    clicks.forEach((c) => {
      const vendor = vendors.find((v) => v.id === c.vendorId);
      const resolvedVendor = vendor?.mainVendor ?? vendor;
      const vendorName = resolvedVendor?.name ?? c.vendorId;
      const typeName = vendor?.type?.name ?? "Unknown";
      if (!map[vendorName]) map[vendorName] = { clients: new Set(), types: {} };
      map[vendorName].clients.add(c.clientId);
      if (!map[vendorName].types[typeName]) map[vendorName].types[typeName] = new Set();
      map[vendorName].types[typeName].add(c.clientId);
    });
    return Object.entries(map)
      .sort((a, b) => b[1].clients.size - a[1].clients.size)
      .map(([name, { clients, types }]) => ({
        name,
        clicks: clients.size,
        types: Object.fromEntries(
          Object.entries(types).map(([t, s]) => [t, s.size])
        ),
      }));
  }, [clicks, vendors]);

  // Dynamic YAxis width based on longest vendor name
  const vendorAxisWidth = useMemo(() => {
    const longest = vendorData.reduce((max, v) => Math.max(max, v.name.length), 0);
    return Math.min(Math.max(longest * 7, 80), 160);
  }, [vendorData]);

  const uniqueClients = useMemo(
    () => new Set(enriched.map((c) => c.clientId)).size,
    [enriched]
  );
  const topVendor = vendorData[0];
  const axisStyle = { fill: "#8b6d46", fontSize: 11 };

  return (
    <ThemeProvider theme={dashTheme}>
      <CssBaseline />
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Syne:wght@600;700;800&family=DM+Mono:wght@300;400;500&display=swap');`}</style>

      <Box sx={{ minHeight: "100vh", bgcolor: "#faead7", p: { xs: 2, md: 4 } }}>

        {/* Header */}
        <Stack direction="row" justifyContent="space-between" alignItems="flex-end" mb={4} flexWrap="wrap" gap={2}>
          <Box>
            <Typography variant="h4" sx={{ color: "#715939", lineHeight: 1 }}>Statistics</Typography>
            <Typography variant="body2" sx={{ color: "#8b6d46", mt: 0.5 }}>
              Analytics dashboard · {enriched.length.toLocaleString()} records loaded
            </Typography>
          </Box>
          <Stack direction="row" spacing={2}>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Vendor</InputLabel>
              <Select value={selectedVendor} label="Vendor" onChange={(e) => setSelectedVendor(e.target.value)}>
                <MenuItem value="all">All Vendors</MenuItem>
                {[...vendors].sort((a, b) => a.name.localeCompare(b.name)).map((v) => <MenuItem key={v.id} value={v.id}>{v.name}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Period</InputLabel>
              <Select value={period} label="Period" onChange={(e) => setPeriod(e.target.value as PeriodKey)}>
                {PERIODS.map((p) => (
                  <MenuItem key={p.key} value={p.key}>{p.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </Stack>

        {/* Error */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
        )}

        {/* Loading */}
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 400 }}>
            <CircularProgress sx={{ color: "#715939" }} />
          </Box>
        ) : (
          <>
            {/* Stat Cards */}
            <Grid container spacing={2} mb={3}>
              <Grid item xs={6} sm={3}>
                <StatCard icon={<MouseIcon fontSize="small" />} label="Total Clicks" value={enriched.length.toLocaleString()} sub={PERIODS.find((p) => p.key === period)?.label} accent="#604c35" />
              </Grid>
              <Grid item xs={6} sm={3}>
                <StatCard icon={<PeopleIcon fontSize="small" />} label="Unique Clients" value={uniqueClients.toLocaleString()} accent="#8b6d46" />
              </Grid>
              <Grid item xs={6} sm={3}>
                <StatCard icon={<StorefrontIcon fontSize="small" />} label="Top Vendor" value={topVendor?.name ?? "—"} sub={`${topVendor?.clicks ?? 0} clicks`} accent="#604c35" />
              </Grid>
              <Grid item xs={6} sm={3}>
                <Card sx={{ height: "100%", position: "relative", overflow: "hidden" }}>
                  <Box sx={{ position: "absolute", top: -20, right: -20, width: 100, height: 100, borderRadius: "50%", background: "radial-gradient(circle, #c09c7222 0%, transparent 70%)" }} />
                  <CardContent sx={{ p: 3 }}>
                    <Typography variant="caption" sx={{ color: "text.secondary", letterSpacing: "0.1em", textTransform: "uppercase", fontSize: "0.65rem" }}>
                      Platforms
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mt: 0.5 }}>
                      <PieChart width={80} height={80}>
                        <Pie data={platformData} cx={35} cy={35} innerRadius={22} outerRadius={36} paddingAngle={2} dataKey="value" strokeWidth={0}>
                          {platformData.map((entry) => (
                            <Cell key={entry.name} fill={PLATFORM_COLORS[entry.name as Platform] || "#c09c72"} />
                          ))}
                        </Pie>
                        <Tooltip content={<CustomTooltip />} />
                      </PieChart>
                      <Box>
                        {platformData.slice(0, 4).map((p) => (
                          <Stack key={p.name} direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.25 }}>
                            <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: PLATFORM_COLORS[p.name as Platform] || "#c09c72", flexShrink: 0 }} />
                            <Typography variant="caption" sx={{ color: "#604c35", fontSize: "0.68rem" }}>
                              {p.name} <span style={{ color: "#715939", fontWeight: 600 }}>{p.value}</span>
                            </Typography>
                          </Stack>
                        ))}
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Row 1: Area chart full width */}
            <Grid container spacing={2} mb={2}>
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="caption" sx={{ color: "text.secondary", letterSpacing: "0.1em", textTransform: "uppercase", fontSize: "0.7rem" }}>
                      Daily Unique Users
                    </Typography>
                    <ResponsiveContainer width="100%" height={240}>
                      <AreaChart data={dailyData} margin={{ top: 16, right: 8, left: -16, bottom: 0 }}>
                        <defs>
                          <linearGradient id="clickGrad" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#c09c72" stopOpacity={0.3} />
                            <stop offset="95%" stopColor="#c09c72" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(113,89,57,0.08)" />
                        <XAxis dataKey="date" tick={axisStyle} axisLine={false} tickLine={false} />
                        <YAxis tick={axisStyle} axisLine={false} tickLine={false} />
                        <Tooltip content={<CustomTooltip />} />
                        <Area type="monotone" dataKey="users" stroke="#c09c72" strokeWidth={2} fill="url(#clickGrad)" dot={false} activeDot={{ r: 4 }} />
                      </AreaChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Row 2: Vendor Bar + Recent Clicks side by side */}
            <Grid container spacing={2} mb={2}>
              <Grid item xs={12} md={5}>
                <Card sx={{ height: "100%" }}>
                  <CardContent>
                    <Typography variant="caption" sx={{ color: "text.secondary", letterSpacing: "0.1em", textTransform: "uppercase", fontSize: "0.7rem" }}>
                      Unique Users by Vendor
                    </Typography>
                    <ResponsiveContainer width="100%" height={Math.max(vendorData.length * 36, 200)}>
                      <BarChart data={vendorData} layout="vertical" barSize={16} margin={{ top: 8, right: 24, left: 8, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(113,89,57,0.08)" horizontal={false} />
                        <XAxis type="number" tick={axisStyle} axisLine={false} tickLine={false} />
                        <YAxis
                          type="category"
                          dataKey="name"
                          tick={{ fill: "#604c35", fontSize: 11, fontFamily: "DM Mono" }}
                          axisLine={false}
                          tickLine={false}
                          width={vendorAxisWidth}
                        />
                        <Tooltip content={<VendorTooltip />} />
                        <Bar dataKey="clicks" fill="#c09c72" radius={[0, 6, 6, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={7}>
                <Card sx={{ height: "100%" }}>
                  <CardContent>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                      <Typography variant="caption" sx={{ color: "text.secondary", letterSpacing: "0.1em", textTransform: "uppercase", fontSize: "0.7rem" }}>
                        Recent Clicks
                      </Typography>
                      <Chip
                        label={`${enriched.length} records`}
                        size="small"
                        sx={{ bgcolor: "rgba(113,89,57,0.1)", color: "#715939", fontFamily: "DM Mono", fontSize: "0.7rem" }}
                      />
                    </Stack>
                    <TableContainer sx={{ maxHeight: Math.max(vendorData.length * 36, 200) }}>
                      <Table stickyHeader size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>ID</TableCell>
                            <TableCell>Clicked At</TableCell>
                            <TableCell>Vendor</TableCell>
                            <TableCell>Client ID</TableCell>
                            <TableCell>Platform</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {enriched.slice(0, 50).map((row) => (
                            <TableRow key={row.id} hover sx={{ "&:hover td": { bgcolor: "rgba(113,89,57,0.05)" } }}>
                              <TableCell sx={{ color: "#715939 !important", fontWeight: 600 }}>{row.id}</TableCell>
                              <TableCell>{new Date(row.clickedAt).toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false })}</TableCell>
                              <TableCell>{row.vendorName}</TableCell>
                              <TableCell sx={{ fontFamily: "DM Mono", fontSize: "0.72rem !important", color: "#5A6070 !important" }}>
                                {row.clientId?.split('-')[0]}
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={row.platform}
                                  size="small"
                                  sx={{
                                    bgcolor: `${PLATFORM_COLORS[row.platform as Platform] || "#888"}18`,
                                    color: PLATFORM_COLORS[row.platform as Platform] || "#888",
                                    fontFamily: "DM Mono",
                                    fontSize: "0.65rem",
                                    height: 20,
                                  }}
                                />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </>
        )}
      </Box>
    </ThemeProvider>
  );
}